import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Grid, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useState } from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import CustomEditRemoveIcon from "../../unit/CustomEditRemoveIcon";
import CustomProductTextField from "../../unit/forms/CustomProductTextField";
import useNotify from "../../unit/useNotify";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function AuthorizationRepayment({
  onSave,
  defaultValues,
  ...props
}: any) {
  const itemKeyRef = React.useRef(0);
  const record = useRecordContext();
  const { success, error, warning, info } = useNotify();
  const sectionName = "authorization-payment";
  const sectionTitle =
    "Authorization and Acknowledgement - Information that requires customer's review or approval";
  const sectionSubtitle = "";

  const defaultAuthorizationPayment = [
    {
      id: "",
      title: "",
      description: "",
    },
  ];

  const [authorizationPayment, setAuthorizationPayment] = useState(
    (
      record?.data?.basic?.authorization?.payment_disclosure ||
      defaultAuthorizationPayment
    ).map((item: any) => ({
      ...item,
      __clientKey: `authorization-payment-${itemKeyRef.current++}`,
    }))
  );
  const subType = record?.data?.basic?.subType;
  const division = record?.data?.basic?.division;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues || {},
  });

  React.useEffect(() => {
    if (record?.data?.basic?.authorization) {
      const nextAuthorizationPayment = (
        record.data.basic.authorization.payment_disclosure ||
        defaultAuthorizationPayment
      ).map((item: any) => ({
        ...item,
        __clientKey: `authorization-payment-${itemKeyRef.current++}`,
      }));

      setAuthorizationPayment(nextAuthorizationPayment);
      reset({
        authorizationPayment: nextAuthorizationPayment,
      });
    }
  }, [record, reset]);
  React.useEffect(() => {
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);

  const hasEmptyAuthorizationPayment = authorizationPayment.some(
    (item: any) => !item.title?.trim() || !item.description?.trim()
  );

  const buildDefaultAuthorizationPayment = () =>
    defaultAuthorizationPayment.map((item) => ({
      ...item,
      __clientKey: `authorization-payment-${itemKeyRef.current++}`,
    }));

  const sanitizeAuthorizationPayment = (items: any[]) =>
    items.map(({ __clientKey, ...item }) => item);

  const persistAuthorizationPayment = (nextAuthorizationPayment: any[]) => {
    const formData: any = getValues();
    formData.saveAction = "authorization";
    formData.saveActionType = "paymentDisclosure";
    formData.authorizationPayment = sanitizeAuthorizationPayment(
      nextAuthorizationPayment
    );

    if (onSave) {
      onSave(formData, props?.curMenu);
      reset({
        ...formData,
        authorizationPayment: nextAuthorizationPayment,
      });
    }
  };

  const addAuthorizationPayment = () => {
    const newAuthorizationPayment = {
      id: "",
      title: "",
      description: "",
      __clientKey: `authorization-payment-${itemKeyRef.current++}`,
    };
    const nextAuthorizationPayment = [
      ...authorizationPayment,
      newAuthorizationPayment,
    ];

    setAuthorizationPayment(nextAuthorizationPayment);
    setValue("authorizationPayment", nextAuthorizationPayment, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const addDefaultAuthorizationPayment = () => {
    const nextAuthorizationPayment = [
      ...authorizationPayment,
      ...buildDefaultAuthorizationPayment(),
    ];
    setAuthorizationPayment(nextAuthorizationPayment);
    setValue("authorizationPayment", nextAuthorizationPayment, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const removeAcknowledgement = (index: any) => {
    if (authorizationPayment.length > 0) {
      const updated = [...authorizationPayment];
      updated.splice(index, 1);
      setAuthorizationPayment(updated);
      setValue("authorizationPayment", updated, { shouldDirty: true });
      persistAuthorizationPayment(updated);
    }
    success("Acknowledgement removed and saved.");
  };

  const handleSaveClick = () => {
    if (hasEmptyAuthorizationPayment) {
      warning("Title and Description cannot be empty.");
      return;
    }

    persistAuthorizationPayment(authorizationPayment);
  };

  const handleAuthorizationPaymentChange = (
    index: any,
    field: any,
    value: any
  ) => {
    const updated = [...authorizationPayment];
    updated[index][field] = value;
    setAuthorizationPayment(updated);
    setValue(`authorizationPayment[${index}].${field}`, value, {
      shouldDirty: true,
    });
  };

  return (
    <SectionStyled id={"section_" + sectionName}>
      <SectionTitle subtitle={sectionSubtitle} title={sectionTitle} />

      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        {productModulesGroup?.includes("disclosure") && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Payment
              </Typography>
              {authorizationPayment.map((item: any, index: any) => (
                <React.Fragment key={item.__clientKey}>
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Grid item xs={10}>
                      <Controller
                        name={`authorizationPayment[${index}].title`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            label="Title"
                            value={item.title}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationPaymentChange(
                                index,
                                "title",
                                e.target.value
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        type="hidden"
                        name={`authorizationPayment[${index}].id`}
                        value={item.id}
                        sx={{ display: "none" }}
                      />
                      {authorizationPayment.length > 0 && (
                        <CustomEditRemoveIcon
                          onClick={() => removeAcknowledgement(index)}
                          tooltip="Remove this acknowledgement"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`authorizationPayment[${index}].description`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="Description"
                            value={item.description}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationPaymentChange(
                                index,
                                "description",
                                e.target.value
                              );
                            }}
                            sx={{ marginTop: "-4px" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {index < authorizationPayment.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addAuthorizationPayment}
                sx={{ width: "120px" }}
              >
                Add
              </Button>
              {/* <Button
                variant="outlined"
                onClick={addDefaultAuthorizationPayment}
                sx={{ width: "140px", ml: 2 }}
              >
                Use Default
              </Button> */}
            </Grid>
          </Grid>
        )}
      </Box>
      {/* Form Actions */}
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || hasEmptyAuthorizationPayment}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
